import { Text } from '@inflearn/ds-react-next';
import { ActionIcon, Checkbox, Divider, Flex, Icon, Stack } from '@inflearn/ds-react-next';
import { faAngleRight } from '@inflearn/pro-regular-svg-icons';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { type UseFormWatch, useFormContext } from 'react-hook-form';

import { AUTH_URL, INFLEARN_APP_LINK } from '../../../constants/urls';
import { useIsWebview } from '../../../hooks/useIsWebview';
import {
  INITIAL_MARKETING_CHANNEL_CONSENT,
  type MarketingChannelConsent,
  MarketingConsentCheckboxes,
  createMarketingChannelConsent,
  isAllMarketingChannelsAgreed,
  isAnyMarketingChannelAgreed,
} from './MarketingConsentCheckboxes';

export interface AgreementFormValues {
  agreedOver14: boolean;
  agreeTerms: boolean;
  agreePrivacy: boolean;
  /** 매체별 동의를 받을 때는 하나라도 동의하면 true */
  allowedMarketing: boolean;
  marketingChannelConsent: MarketingChannelConsent;
}

export const INITIAL_AGREEMENT_VALUES: AgreementFormValues = {
  agreedOver14: false,
  agreeTerms: false,
  agreePrivacy: false,
  allowedMarketing: false,
  marketingChannelConsent: INITIAL_MARKETING_CHANNEL_CONSENT,
};

export const useIsAgreementValid = <T extends AgreementFormValues>(watch: UseFormWatch<T>) => {
  const values = watch();

  return values.agreeTerms && values.agreePrivacy && values.agreedOver14;
};

interface SignupAgreementSectionProps {
  /** true면 광고성 정보 수신 동의를 매체(이메일·카카오톡·문자)별로 받는다. */
  showMarketingChannels?: boolean;
}

export const SignupAgreementSection = ({
  showMarketingChannels = false,
}: SignupAgreementSectionProps) => {
  const { t } = useTranslation();
  const isWebview = useIsWebview();
  const { watch, setValue } = useFormContext<AgreementFormValues>();

  const agreedOver14 = watch('agreedOver14');
  const agreeTerms = watch('agreeTerms');
  const agreePrivacy = watch('agreePrivacy');
  const allowedMarketing = watch('allowedMarketing');
  const marketingChannelConsent = watch('marketingChannelConsent');

  const isMarketingFullyAgreed = showMarketingChannels
    ? isAllMarketingChannelsAgreed(marketingChannelConsent)
    : allowedMarketing;
  const allChecked = agreedOver14 && agreeTerms && agreePrivacy && isMarketingFullyAgreed;

  const handleMarketingChannelConsentChange = (consent: MarketingChannelConsent) => {
    setValue('marketingChannelConsent', consent);
    setValue('allowedMarketing', isAnyMarketingChannelAgreed(consent));
  };

  const handleAllChange = (checked: boolean) => {
    setValue('agreedOver14', checked);
    setValue('agreeTerms', checked);
    setValue('agreePrivacy', checked);
    handleMarketingChannelConsentChange(createMarketingChannelConsent(checked));
  };

  const integratedAccountPolicyHref = isWebview
    ? INFLEARN_APP_LINK.INTEGRATED_TERMS
    : AUTH_URL.INTEGRATED_ACCOUNT_POLICY();

  const privacyHref = isWebview ? INFLEARN_APP_LINK.PRIVACY_POLICY : AUTH_URL.INFLEARN_PRIVACY();

  return (
    <Flex direction="column" gap={8}>
      <Stack gap={5}>
        <Checkbox
          checked={allChecked}
          onChange={(e) => handleAllChange(e.currentTarget.checked)}
          label={t('전체 동의')}
          size="sm"
          radius="sm"
        />
        <Text size="xs" color="gray.6" pl={32}>
          {t('선택항목에 대해 동의하지 않아도 회원가입 및 일반적인 서비스를 이용할 수 있습니다.')}
        </Text>
      </Stack>
      <Divider />
      <Flex direction="column" gap={8}>
        <Checkbox
          checked={agreedOver14}
          onChange={(e) => setValue('agreedOver14', e.currentTarget.checked)}
          label={t('signup.agreement.age_over_14')}
          size="sm"
          radius="sm"
          py={5}
        />
        <Flex align="center" justify="space-between" py={5}>
          <Checkbox
            checked={agreeTerms}
            onChange={(e) => setValue('agreeTerms', e.currentTarget.checked)}
            label={t('(필수) 통합 계정 및 서비스 이용약관 동의')}
            size="sm"
            radius="sm"
          />
          <ActionIcon
            component={Link}
            href={integratedAccountPolicyHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('(필수) 통합 계정 및 서비스 이용약관 동의')}
            variant="transparent"
            color="gray.6"
            size="sm"
          >
            <Icon icon={faAngleRight} />
          </ActionIcon>
        </Flex>
        <Flex align="center" justify="space-between" py={5}>
          <Checkbox
            checked={agreePrivacy}
            onChange={(e) => setValue('agreePrivacy', e.currentTarget.checked)}
            label={t('(필수) 개인정보 수집 이용 동의')}
            size="sm"
            radius="sm"
          />
          <ActionIcon
            component={Link}
            href={privacyHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('(필수) 개인정보 수집 이용 동의')}
            variant="transparent"
            color="gray.6"
            size="sm"
          >
            <Icon icon={faAngleRight} />
          </ActionIcon>
        </Flex>
        {showMarketingChannels ? (
          <MarketingConsentCheckboxes
            value={marketingChannelConsent}
            onChange={handleMarketingChannelConsentChange}
          />
        ) : (
          <Checkbox
            checked={allowedMarketing}
            onChange={(e) => setValue('allowedMarketing', e.currentTarget.checked)}
            label={t('(선택) 광고성 정보 수신 동의')}
            size="sm"
            radius="sm"
            py={5}
          />
        )}
      </Flex>
    </Flex>
  );
};
